/**
 * Enums do contrato do portal do assinante.
 *
 * Espelham os valores que `subscriber/*` devolve (cakto-backend):
 * `SubscriptionStatus` e `PaymentStatus` em `gateway/enums.py`, `PaymentMethodType` em
 * `gateway/models.py`. São strings do backend, não rótulos — a tradução vive no i18n.
 *
 * Ficam aqui, e não no serviço, porque são contrato: a UI compara contra eles em vários
 * pontos e não deve importar nada de uma camada de dados que pode mudar de implementação.
 */

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
  PAUSED: 'paused',
  LATE: 'late',
  TRIAL: 'trial',
};

/**
 * `charge.status` do `update-card` quando a assinatura está inadimplente: salvar o cartão cobra
 * o ciclo em aberto, e este é o desfecho dessa cobrança.
 */
export const OPEN_CYCLE_CHARGE_STATUS = {
  PAID: 'paid',
  PROCESSING: 'processing',
  NOTHING_TO_CHARGE: 'nothing_to_charge',
};

/**
 * `code` do 503 que toda rota do portal devolve com a switch `subscriber_portal_enabled`
 * desligada. É ele que separa o desligamento proposital de um 503 de proxy ou de deploy.
 */
export const SUBSCRIBER_PORTAL_DISABLED_CODE = 'subscriber_portal_disabled';

/**
 * `code` das recusas do portal (`_refused` em `gateway/subscriber_views.py`, CKTMF-446). O
 * `detail` que vem junto é texto em pt-BR e pode mudar; quem escolhe a mensagem é o `code`.
 */
export const PORTAL_ERROR_CODE = {
  OTP_INVALID: 'otp_invalid',
  OTP_LOCKED: 'otp_locked',
  PHONE_LOGIN_UNAVAILABLE: 'phone_login_unavailable',
  SUBSCRIPTION_NOT_LINKED: 'subscription_not_linked',
  SUBSCRIPTION_WITHOUT_FUTURE_CHARGE: 'subscription_without_future_charge',
  CHARGE_IN_PROGRESS: 'charge_in_progress',
  PAYMENT_METHOD_NOT_ENABLED: 'payment_method_not_enabled',
  SUBSCRIPTION_NOT_DELINQUENT: 'subscription_not_delinquent',
  PIX_UNAVAILABLE: 'pix_unavailable',
  PIX_PAYMENT_PENDING: 'pix_payment_pending',
  PIX_AUTO_DISABLED: 'pix_auto_disabled',
  PIX_AUTO_NOT_DELINQUENT: 'pix_auto_not_delinquent',
  PIX_AUTO_UNAVAILABLE: 'pix_auto_unavailable',
  PIX_AUTO_UNAVAILABLE_ACQUIRER: 'pix_auto_unavailable_acquirer',
  PIX_AUTO_ALREADY_ACTIVE: 'pix_auto_already_active',
  PIX_AUTO_AUTHORIZATION_PENDING: 'pix_auto_authorization_pending',
  EMAIL_SCOPE_REQUIRED: 'email_scope_required',
  SUBSCRIPTION_ALREADY_CANCELED: 'subscription_already_canceled',
  SUBSCRIPTION_NOT_CANCELABLE: 'subscription_not_cancelable',
  SUBSCRIBER_CANCEL_DISABLED: 'subscriber_cancel_disabled',
  SUBSCRIBER_CANCEL_LATE_DISABLED: 'subscriber_cancel_late_disabled',
  INVALID_CANCELLATION_REASON: 'invalid_cancellation_reason',
  CANCEL_UNAVAILABLE: 'cancel_unavailable',
  // Link sem login do aviso de cobrança expirado, revogado ou que não existe.
  LINK_INVALID: 'link_invalid',
};

/**
 * `reason` do cancelamento: `SubscriptionCancellationFeedback` em `gateway/enums.py` (CKTMF-485).
 * Nenhuma rota devolve esta lista, e um valor fora dela volta 400 `invalid_cancellation_reason`.
 * Sem motivo, o campo não vai, e o backend grava que o assinante não disse.
 */
export const CANCELLATION_REASON = {
  TOO_EXPENSIVE: 'too_expensive',
  NOT_USING: 'not_using',
  NOT_AS_EXPECTED: 'not_as_expected',
  ACCESS_PROBLEM: 'access_problem',
  OTHER: 'other',
};

/**
 * Chave de `subscriberFlow.cancelDialog.reasons` de cada motivo. O painel do produtor mostra o
 * motivo com o texto que o assinante escolheu no portal (CKTMF-488), e um mapa só impede que os
 * dois digam coisas diferentes quando entrar um motivo novo.
 */
export const CANCELLATION_REASON_LABEL_KEY = {
  [CANCELLATION_REASON.TOO_EXPENSIVE]: 'tooExpensive',
  [CANCELLATION_REASON.NOT_USING]: 'notUsing',
  [CANCELLATION_REASON.NOT_AS_EXPECTED]: 'notAsExpected',
  [CANCELLATION_REASON.ACCESS_PROBLEM]: 'accessProblem',
  [CANCELLATION_REASON.OTHER]: 'other',
};

/** `channel` do `get_token`: por onde o código saiu. Login por telefone sai por WhatsApp. */
export const CODE_CHANNEL = {
  EMAIL: 'email',
  WHATSAPP: 'whatsapp',
};

/** `reason` do 422 do `update-card`. É o contrato da recusa; o `detail` é só texto. */
export const CARD_REFUSAL_REASON = {
  INFRASTRUCTURE: 'infrastructure',
  CARD_NOT_PROCESSED: 'card_not_processed',
  ISSUER_TIMED: 'issuer_timed',
  ISSUER: 'issuer',
  NOT_APPROVED: 'not_approved',
};

/**
 * `paymentMethod` da assinatura. `pix` e `boleto` só chegam desde que o backend deixou de rotular
 * a renovação por QR como cartão; só os três primeiros aparecem em `availablePaymentMethods`.
 */
export const PAYMENT_METHOD = {
  CREDIT_CARD: 'credit_card',
  AUTOMATIC_PIX: 'pix_auto',
  PIX: 'pix',
  BOLETO: 'boleto',
};

/**
 * Ordem dos botões da tela de pagamento. O backend já devolve `availablePaymentMethods` nela, mas
 * a tela reordena para não depender disso.
 */
export const PAYMENT_METHOD_ORDER = [
  PAYMENT_METHOD.CREDIT_CARD,
  PAYMENT_METHOD.AUTOMATIC_PIX,
  PAYMENT_METHOD.PIX,
];

/** `status` do Pix gerado pelo portal (`pay-pix/`). Só `waiting_payment` ainda pode ser pago. */
export const PORTAL_PIX_STATUS = {
  WAITING_PAYMENT: 'waiting_payment',
  PAID: 'paid',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
};

/**
 * `from` do link que o backend manda ao portal (`subscriber_portal_payment_url`). `email` fica para
 * sempre: há links de recusa e da base zumbi nas caixas de entrada, e eles não podem perder o aviso.
 */
export const PORTAL_LINK_ORIGIN = {
  RECOVERY_EMAIL: 'email',
  RENEWAL_REMINDER: 'renewal_reminder',
};

/**
 * Aviso da tela de pagamento de cada origem. Um texto inteiro por origem, nunca uma frase com a
 * origem de variável: o de recuperação fala de pendência, e o lembrete vai a quem está em dia.
 *
 * Cada texto só é verdade num estado da assinatura. O link fica na caixa de entrada depois de o
 * estado mudar: a recuperação aberta depois de pagar, o lembrete aberto depois da recusa ou do
 * cancelamento. Nesses casos a tela não mostra aviso, em vez de contradizer o resto dela.
 * - `whenPendingPayment`: o texto vale com a assinatura pendente (`true`) ou em dia (`false`).
 * - `whenNextChargeChangeable`: o texto promete trocar o meio antes da próxima cobrança, então
 *   precisa de cobrança futura e de algum meio oferecido.
 */
export const PORTAL_LINK_ORIGIN_CALLOUT = {
  [PORTAL_LINK_ORIGIN.RECOVERY_EMAIL]: {
    messageKey: 'subscriberFlow.paymentPage.callouts.recoveryEmail',
    whenPendingPayment: true,
  },
  [PORTAL_LINK_ORIGIN.RENEWAL_REMINDER]: {
    messageKey: 'subscriberFlow.paymentPage.callouts.renewalReminder',
    whenPendingPayment: false,
    whenNextChargeChangeable: true,
  },
};

/** `delinquency.lateReason` que muda o texto da tela. Os demais seguem a mensagem genérica. */
export const LATE_REASON = {
  PIX_RENEWAL_EXPIRED: 'pix_renewal_expired',
};

/**
 * `PaymentStatus` (gateway/models.py) inteiro: o histórico do assinante não filtra o status, e
 * uma cobrança pode chegar em qualquer um. `MED` é maiúsculo no backend.
 */
export const CHARGE_STATUS = {
  PROCESSING: 'processing',
  AUTHORIZED: 'authorized',
  PAID: 'paid',
  REFUND_REQUESTED: 'refund_requested',
  IN_SETTLEMENT: 'in_settlement',
  ACQUIRER_ERROR: 'acquirer_error',
  REFUNDED: 'refunded',
  WAITING_PAYMENT: 'waiting_payment',
  REFUSED: 'refused',
  BLOCKED: 'blocked',
  CHARGEDBACK: 'chargedback',
  CANCELED: 'canceled',
  IN_PROTEST: 'in_protest',
  PARTIALLY_PAID: 'partially_paid',
  PRECHARGEBACK: 'prechargeback',
  SCHEDULED: 'scheduled',
  RETRYING: 'retrying',
  MED: 'MED',
};
